// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase Bill (PB) helpers (Stage 6P.4).
//
// Pure ESM module — exercised by the frontend node:test runner.
//
// Frontend mirror of backend Purchase Bill math + payload shaping.
// The backend (`backend/src/services/erp/purchase/
// purchaseBillCalculation.service.js` and `purchaseBill.service.js`)
// remains the source of truth for bill_sqmt / bill_sqft /
// bill_total_length, gross / discount / taxable / CGST / SGST /
// IGST and the DRAFT → MATCHED / MISMATCH_HOLD / APPROVED transitions.
// These helpers exist only to give the bill editor instant feedback
// while the operator types; the API recomputes on every save and
// rejects mismatches.
//
// Mirrors the style of:
//   - frontend/src/pages/erp/purchase/grnHelpers.js
//     (option lists, normalize/build, validation shape, capability gates)
//   - backend/src/services/erp/purchase/purchaseBillCalculation.service.js
//     (tolerant numeric coercion, round2/round4, line math, tax split,
//     snake_case ⇄ camelCase access).
// ─────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────
// Enum mirrors — kept aligned with backend
// services/erp/purchase/purchaseBill.constants.js.
// Frozen so callers can't mutate the shared lists by accident.
// ─────────────────────────────────────────────────────────────────

export const ITEM_TYPES = Object.freeze([
  'GLASS',
  'HARDWARE',
  'ACCESSORY',
  'PROFILE',
  'UPVC',
  'ALUMINIUM',
  'SERVICE',
  'JOBWORK',
  'OTHER'
]);

// Length-based UOMs — see backend purchaseBill.constants.LENGTH_BASED_UOMS.
export const LENGTH_BASED_UOMS = Object.freeze(['RFT', 'RMT', 'MTR']);

// Profile family — sub-set of item types whose lines are driven by
// length × pieces math (PROFILE / UPVC / ALUMINIUM share a code path).
export const PROFILE_FAMILY_TYPES = Object.freeze(['PROFILE', 'UPVC', 'ALUMINIUM']);

// Glass family — sub-set of item types whose lines run through the
// sqmt ↔ sqft area conversion path.
export const GLASS_FAMILY_TYPES = Object.freeze(['GLASS']);

// Simple-quantity family — item types whose lines are plain
// qty × rate (no area, no per-length conversion).
export const SIMPLE_QTY_TYPES = Object.freeze([
  'HARDWARE',
  'ACCESSORY',
  'SERVICE',
  'JOBWORK',
  'OTHER'
]);

// Bill header statuses — mirrors backend PB_STATUSES.
export const PB_STATUSES = Object.freeze([
  'DRAFT',
  'MATCHED',
  'MISMATCH_HOLD',
  'APPROVED',
  'POSTING_PENDING',
  'CANCELLED'
]);

// Header / line match statuses — mirrors backend PB_MATCH_STATUSES.
export const PB_MATCH_STATUSES = Object.freeze([
  'PENDING',
  'MATCHED',
  'MISMATCH',
  'OVERRIDE_REQUIRED'
]);

// Matching modes — TWO_WAY (PO ↔ Bill) or THREE_WAY (PO ↔ GRN ↔ Bill).
export const PB_MATCHING_TYPES = Object.freeze(['TWO_WAY', 'THREE_WAY']);

// mm² → m² divisor — keep symbolic so it reads at the call site.
export const SQMT_DIVISOR = 1000000;

// 1 SQMT = 10.764 SQFT — matches backend SQFT_PER_SQMT.
export const SQFT_PER_SQMT = 10.764;

// GST rate sanity bounds — validators clamp/reject outside this band.
export const GST_RATE_MIN = 0;
export const GST_RATE_MAX = 28;


// ─────────────────────────────────────────────────────────────────
// Private constants used by the math + validation helpers
// ─────────────────────────────────────────────────────────────────

const ITEM_TYPE_GLASS = 'GLASS';

const GLASS_TYPES_SET   = new Set(GLASS_FAMILY_TYPES);
const PROFILE_TYPES_SET = new Set(PROFILE_FAMILY_TYPES);
const SIMPLE_TYPES_SET  = new Set(SIMPLE_QTY_TYPES);
const KNOWN_TYPES_SET   = new Set(ITEM_TYPES);
const LENGTH_UOMS_SET   = new Set(LENGTH_BASED_UOMS);

const PB_STATUS_SET       = new Set(PB_STATUSES);
const PB_MATCH_STATUS_SET = new Set(PB_MATCH_STATUSES);
const PB_MATCHING_TYPE_SET = new Set(PB_MATCHING_TYPES);

const UOM_SQMT = 'SQMT';
const UOM_SQFT = 'SQFT';
const UOM_PCS  = 'PCS';


// ─────────────────────────────────────────────────────────────────
// Tolerant numeric coercion — mirrors backend toNumber.
// Null / undefined / empty string / non-finite all collapse to 0.
// Never throws, never returns NaN.
// ─────────────────────────────────────────────────────────────────

function toNumber(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return 0;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : 0;
  }
  if (typeof value === 'boolean') return value ? 1 : 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// Half-up rounding — defeats IEEE-754 surprises like 1.005 → 1.0049…
// so 1.005 rounds to 1.01, not 1.00, regardless of sign. Mirrors the
// rounding behaviour of grnHelpers / purchaseHelpers so a bill that
// references the same source data lines up to the paisa.
function roundHalfUp(value, decimals) {
  const n = toNumber(value);
  if (n === 0) return 0;
  const factor = Math.pow(10, decimals);
  const sign   = n < 0 ? -1 : 1;
  const abs    = Math.abs(n);
  const scaled = abs * factor + 1e-9;
  return (sign * Math.round(scaled)) / factor;
}

export function round2(n) { return roundHalfUp(n, 2); }
export function round4(n) { return roundHalfUp(n, 4); }


// ─────────────────────────────────────────────────────────────────
// Snake / camel access — lines arrive from both shapes (grid edits,
// API responses, tests). We probe both and never mutate the caller.
// ─────────────────────────────────────────────────────────────────

function pick(obj, snake, camel, fallback) {
  if (!obj || typeof obj !== 'object') return fallback;
  if (snake in obj && obj[snake] !== undefined) return obj[snake];
  if (camel in obj && obj[camel] !== undefined) return obj[camel];
  return fallback;
}

function pickStringUpper(obj, snake, camel) {
  const v = pick(obj, snake, camel, '');
  if (v === null || v === undefined) return '';
  return String(v).trim().toUpperCase();
}

function pickStringTrim(obj, snake, camel) {
  const v = pick(obj, snake, camel, '');
  if (v === null || v === undefined) return '';
  return String(v).trim();
}


// ─────────────────────────────────────────────────────────────────
// Bill measurement helpers — exported for the PB editor to drive
// instant recalculation as the operator types width / height / qty /
// length_value. Mirrors the backend purchaseBillCalculation.service
// formulas bit-for-bit.
// ─────────────────────────────────────────────────────────────────

// Bill glass area: widthMm × heightMm × qty ÷ 1,000,000 (mm² → m²).
export function calculateBillSqmt(widthMm, heightMm, qty) {
  const w = toNumber(widthMm);
  const h = toNumber(heightMm);
  const q = toNumber(qty);
  if (w <= 0 || h <= 0 || q <= 0) return 0;
  return round4((w * h * q) / SQMT_DIVISOR);
}

export function calculateBillSqft(sqmt) {
  const s = toNumber(sqmt);
  if (s <= 0) return 0;
  return round4(s * SQFT_PER_SQMT);
}

// length_value × qty for length-driven items. UOM choice is
// validated upstream — this helper just multiplies.
export function calculateBillTotalLength(lengthValue, qty) {
  const l = toNumber(lengthValue);
  const q = toNumber(qty);
  if (l <= 0 || q <= 0) return 0;
  return round4(l * q);
}


// ─────────────────────────────────────────────────────────────────
// Rate basis selection — picks the right multiplicand for the line's
// gross amount based on item_type + UOM. Mirrors backend
// purchaseBillCalculation.selectGrossBasis.
//
//   GLASS + SQMT → bill_sqmt × rate
//   GLASS + SQFT → bill_sqft × rate
//   GLASS + PCS  → bill_qty × rate
//   GLASS + other → bill_qty × rate (defensive)
//   PROFILE/UPVC/ALUMINIUM + RFT/RMT/MTR → bill_total_length × rate
//   PROFILE/UPVC/ALUMINIUM + anything else → bill_qty × rate
//   SIMPLE qty / SERVICE / JOBWORK / OTHER / unknown → bill_qty × rate
// ─────────────────────────────────────────────────────────────────

function selectGrossBasis(line, itemType, uom) {
  const billQty   = toNumber(pick(line, 'bill_qty', 'billQty', 0));
  const billSqmt  = toNumber(pick(line, 'bill_sqmt', 'billSqmt', 0));
  const billSqft  = toNumber(pick(line, 'bill_sqft', 'billSqft', 0));
  const billTotal = toNumber(pick(line, 'bill_total_length', 'billTotalLength', 0));

  if (GLASS_TYPES_SET.has(itemType)) {
    if (uom === UOM_SQMT) return billSqmt;
    if (uom === UOM_SQFT) return billSqft;
    if (uom === UOM_PCS)  return billQty;
    return billQty; // defensive
  }
  if (PROFILE_TYPES_SET.has(itemType)) {
    if (LENGTH_UOMS_SET.has(uom)) return billTotal;
    return billQty;
  }
  if (SIMPLE_TYPES_SET.has(itemType)) return billQty;
  return billQty;
}


// ─────────────────────────────────────────────────────────────────
// Per-line bill math — mirrors backend calculatePurchaseBillLineAmount.
// Normalises measurements first (zero stale glass dims on profile
// lines and vice versa), then applies:
//
//   gross    = round2(basis × rate)
//   discount = round2(gross × discount_percent / 100)
//   taxable  = round2(gross − discount)
//   gst      = round2(taxable × gst_rate / 100)
//
// Tax split:
//   - same state (supplier and company both present and equal)
//     → CGST = SGST = round2(gst / 2); the second half is computed
//     as (gst − cgst) so a half-paisa remainder lands on SGST rather
//     than vanishing.
//   - different state OR either state missing → IGST = gst.
//
// Returns a NEW line carrying both casings; callers can spread it
// back into grid state without translating field names.
// ─────────────────────────────────────────────────────────────────

export function calculatePurchaseBillLine(line, taxContext) {
  const safe       = line && typeof line === 'object' ? line : {};
  const ctx        = taxContext && typeof taxContext === 'object' ? taxContext : {};
  const itemTypeIn = pickStringUpper(safe, 'item_type', 'itemType');
  const itemType   = KNOWN_TYPES_SET.has(itemTypeIn) ? itemTypeIn : itemTypeIn;
  const uom        = pickStringUpper(safe, 'uom', 'uom');

  const billQty  = toNumber(pick(safe, 'bill_qty', 'billQty', 0));
  const rate     = toNumber(pick(safe, 'bill_rate', 'billRate',
                       pick(safe, 'rate', 'rate', 0)));
  const discountPct = toNumber(pick(safe, 'discount_percent', 'discountPercent', 0));
  const gstRate     = toNumber(pick(safe, 'gst_rate', 'gstRate', 0));

  let widthMm   = toNumber(pick(safe, 'bill_width_mm', 'billWidthMm',
                       pick(safe, 'width_mm', 'widthMm', 0)));
  let heightMm  = toNumber(pick(safe, 'bill_height_mm', 'billHeightMm',
                       pick(safe, 'height_mm', 'heightMm', 0)));
  let lengthVal = toNumber(pick(safe, 'bill_length_value', 'billLengthValue',
                       pick(safe, 'length_value', 'lengthValue', 0)));

  let billSqmt        = 0;
  let billSqft        = 0;
  let billTotalLength = 0;

  if (GLASS_TYPES_SET.has(itemType)) {
    billSqmt        = calculateBillSqmt(widthMm, heightMm, billQty);
    billSqft        = calculateBillSqft(billSqmt);
    billTotalLength = 0;
    lengthVal = 0;
  } else if (PROFILE_TYPES_SET.has(itemType)) {
    billTotalLength = calculateBillTotalLength(lengthVal, billQty);
    billSqmt        = 0;
    billSqft        = 0;
    widthMm  = 0;
    heightMm = 0;
  } else {
    // SIMPLE qty / SERVICE / JOBWORK / OTHER / unknown — qty only.
    billSqmt        = 0;
    billSqft        = 0;
    billTotalLength = 0;
    widthMm   = 0;
    heightMm  = 0;
    lengthVal = 0;
  }

  // Now compute gross from the normalised basis.
  const basis = selectGrossBasis(
    { bill_qty: billQty, bill_sqmt: billSqmt, bill_sqft: billSqft, bill_total_length: billTotalLength },
    itemType,
    uom
  );

  const grossAmount    = round2(basis * rate);
  const discountAmount = round2(grossAmount * discountPct / 100);
  const taxableAmount  = round2(grossAmount - discountAmount);
  const gstAmount      = round2(taxableAmount * gstRate / 100);

  const supplierState = pickStringUpper(ctx, 'supplier_state_code', 'supplierStateCode');
  const companyState  = pickStringUpper(ctx, 'company_state_code',  'companyStateCode');
  const sameState =
    supplierState && companyState && supplierState === companyState;

  let cgstAmount = 0;
  let sgstAmount = 0;
  let igstAmount = 0;
  if (sameState) {
    const half = round2(gstAmount / 2);
    cgstAmount = half;
    sgstAmount = round2(gstAmount - half);
  } else {
    igstAmount = gstAmount;
  }

  const lineTotal = round2(taxableAmount + cgstAmount + sgstAmount + igstAmount);

  // Stable client row id — needed by the grid before the server has
  // assigned a UUID. Reuse any existing id so re-normalising a row
  // doesn't churn the React key.
  const cid =
    safe._clientRowId ||
    (safe.id ? `pb-${safe.id}` : `pb-c-${nextRowSeq()}`);

  return Object.assign({}, safe, {
    _clientRowId: cid,
    _dirty:       safe._dirty === true,

    item_type:          itemType || null,
    itemType:           itemType || null,
    uom:                uom || null,
    bill_qty:           billQty,
    billQty,
    bill_rate:          rate,
    billRate:           rate,
    rate,
    discount_percent:   discountPct,
    discountPercent:    discountPct,
    gst_rate:           gstRate,
    gstRate,

    bill_width_mm:      widthMm,
    billWidthMm:        widthMm,
    width_mm:           widthMm,
    bill_height_mm:     heightMm,
    billHeightMm:       heightMm,
    height_mm:          heightMm,
    bill_length_value:  lengthVal,
    billLengthValue:    lengthVal,
    length_value:       lengthVal,

    bill_sqmt:          billSqmt,
    billSqmt,
    bill_sqft:          billSqft,
    billSqft,
    bill_total_length:  billTotalLength,
    billTotalLength,
    total_length:       billTotalLength,

    gross_amount:       grossAmount,
    grossAmount,
    discount_amount:    discountAmount,
    discountAmount,
    taxable_amount:     taxableAmount,
    taxableAmount,
    cgst_amount:        cgstAmount,
    cgstAmount,
    sgst_amount:        sgstAmount,
    sgstAmount,
    igst_amount:        igstAmount,
    igstAmount,
    total_amount:       lineTotal,
    totalAmount:        lineTotal,
    line_total:         lineTotal,
    lineTotal
  });
}


// ─────────────────────────────────────────────────────────────────
// Header totals — mirrors backend calculatePurchaseBillTotals.
//
// Accepts either pre-computed lines (gross_amount, taxable_amount,
// cgst_amount, …) or raw lines + a tax context on options; in the
// latter case each line is recomputed via calculatePurchaseBillLine.
//
// round_off is operator-supplied; treated as a signed adjustment
// applied to grand_total only. Subtotal / tax buckets are never
// touched by it.
// ─────────────────────────────────────────────────────────────────

export function calculatePurchaseBillTotals(lines, options) {
  const opts          = options && typeof options === 'object' ? options : {};
  const list          = Array.isArray(lines) ? lines : [];
  const roundOff      = toNumber(pick(opts, 'round_off', 'roundOff', 0));
  const supplierState = pickStringUpper(opts, 'supplier_state_code', 'supplierStateCode');
  const companyState  = pickStringUpper(opts, 'company_state_code',  'companyStateCode');

  const taxCtx = {
    supplier_state_code: supplierState,
    company_state_code:  companyState
  };

  let subtotal       = 0;
  let discountAmount = 0;
  let taxableAmount  = 0;
  let cgstAmount     = 0;
  let sgstAmount     = 0;
  let igstAmount     = 0;
  let totalLines     = 0;

  for (const ln of list) {
    if (!ln || typeof ln !== 'object') continue;
    totalLines += 1;

    // If the line already carries pre-computed amounts, trust them.
    // Otherwise recompute on the fly using the header-supplied tax
    // context so callers can pass raw rows without an extra map().
    const hasPrecomputed =
      ('gross_amount'    in ln && ln.gross_amount    !== undefined) ||
      ('grossAmount'     in ln && ln.grossAmount     !== undefined) ||
      ('taxable_amount'  in ln && ln.taxable_amount  !== undefined) ||
      ('taxableAmount'   in ln && ln.taxableAmount   !== undefined);

    const computed = hasPrecomputed ? ln : calculatePurchaseBillLine(ln, taxCtx);

    subtotal       += toNumber(pick(computed, 'gross_amount',    'grossAmount',    0));
    discountAmount += toNumber(pick(computed, 'discount_amount', 'discountAmount', 0));
    taxableAmount  += toNumber(pick(computed, 'taxable_amount',  'taxableAmount',  0));
    cgstAmount     += toNumber(pick(computed, 'cgst_amount',     'cgstAmount',     0));
    sgstAmount     += toNumber(pick(computed, 'sgst_amount',     'sgstAmount',     0));
    igstAmount     += toNumber(pick(computed, 'igst_amount',     'igstAmount',     0));
  }

  subtotal       = round2(subtotal);
  discountAmount = round2(discountAmount);
  taxableAmount  = round2(taxableAmount);
  cgstAmount     = round2(cgstAmount);
  sgstAmount     = round2(sgstAmount);
  igstAmount     = round2(igstAmount);

  const totalTax   = round2(cgstAmount + sgstAmount + igstAmount);
  const roundOffR  = round2(roundOff);
  const grandTotal = round2(taxableAmount + totalTax + roundOffR);

  return {
    totalLines,
    subtotal,
    discount_amount:     discountAmount,
    discountAmount,
    taxable_amount:      taxableAmount,
    taxableAmount,
    cgst_amount:         cgstAmount,
    cgstAmount,
    sgst_amount:         sgstAmount,
    sgstAmount,
    igst_amount:         igstAmount,
    igstAmount,
    total_tax:           totalTax,
    totalTax,
    round_off:           roundOffR,
    roundOff:            roundOffR,
    grand_total:         grandTotal,
    grandTotal,
    supplier_state_code: supplierState || null,
    supplierStateCode:   supplierState || null,
    company_state_code:  companyState  || null,
    companyStateCode:    companyState  || null
  };
}


// ─────────────────────────────────────────────────────────────────
// Stable client row ids — minted only when the editor doesn't already
// carry one. Mirrors the grn-c-N pattern used by grnHelpers.
// ─────────────────────────────────────────────────────────────────

let _rowIdCounter = 0;
function nextRowSeq() {
  _rowIdCounter += 1;
  return _rowIdCounter;
}


// ─────────────────────────────────────────────────────────────────
// PO → Bill seeding — given a list of PO lines, return Bill line
// drafts with bill_qty defaulted to ordered qty, rate / discount /
// gst_rate copied from the PO line, and bill measurements copied
// from the PO line so the editor doesn't have to re-type them.
//
// `defaults` may carry `{ supplierStateCode, companyStateCode,
// matchingType }` — the tax context is forwarded to the per-line
// math so seeded rows arrive with totals already populated.
// ─────────────────────────────────────────────────────────────────

export function buildBillFromPoLines(poLines, defaults) {
  const list = Array.isArray(poLines) ? poLines : [];
  const opts = defaults && typeof defaults === 'object' ? defaults : {};
  const taxCtx = {
    supplier_state_code: pickStringUpper(opts, 'supplier_state_code', 'supplierStateCode'),
    company_state_code:  pickStringUpper(opts, 'company_state_code',  'companyStateCode')
  };

  return list.map((po) => {
    const safe       = po && typeof po === 'object' ? po : {};
    const poLineId   = safe.id || pick(safe, 'po_line_id', 'poLineId',
                          pick(safe, 'purchase_order_line_id', 'purchaseOrderLineId', null));
    const itemType   = pickStringUpper(safe, 'item_type', 'itemType');
    const uom        = pickStringUpper(safe, 'uom', 'uom');
    const orderedQty = toNumber(pick(safe, 'qty', 'qty', 0));
    const widthMm    = toNumber(pick(safe, 'width_mm',     'widthMm',     0));
    const heightMm   = toNumber(pick(safe, 'height_mm',    'heightMm',    0));
    const lengthVal  = toNumber(pick(safe, 'length_value', 'lengthValue', 0));
    const rate       = toNumber(pick(safe, 'rate',         'rate',        0));
    const discountPct = toNumber(pick(safe, 'discount_percent', 'discountPercent', 0));
    const gstRate    = toNumber(pick(safe, 'gst_rate',     'gstRate',     0));

    const draft = {
      purchase_order_line_id: poLineId,
      purchaseOrderLineId:    poLineId,

      item_id:    pick(safe, 'item_id',   'itemId',   null),
      itemId:     pick(safe, 'item_id',   'itemId',   null),
      item_code:  pick(safe, 'item_code', 'itemCode', null),
      itemCode:   pick(safe, 'item_code', 'itemCode', null),
      item_name:  pick(safe, 'item_name', 'itemName', null),
      itemName:   pick(safe, 'item_name', 'itemName', null),
      item_type:  itemType || null,
      itemType:   itemType || null,
      uom:        uom || null,

      bill_qty:          orderedQty,
      billQty:           orderedQty,
      ordered_qty:       orderedQty,
      orderedQty,

      bill_rate:         rate,
      billRate:          rate,
      rate,
      discount_percent:  discountPct,
      discountPercent:   discountPct,
      gst_rate:          gstRate,
      gstRate,

      bill_width_mm:     widthMm,
      billWidthMm:       widthMm,
      bill_height_mm:    heightMm,
      billHeightMm:      heightMm,
      bill_length_value: lengthVal,
      billLengthValue:   lengthVal,

      hsn_code: pick(safe, 'hsn_code', 'hsnCode', null),
      sac_code: pick(safe, 'sac_code', 'sacCode', null),

      remarks: null
    };
    return calculatePurchaseBillLine(draft, taxCtx);
  });
}


// ─────────────────────────────────────────────────────────────────
// GRN → Bill seeding — given a list of GRN lines, return Bill line
// drafts with bill_qty defaulted to accepted_qty (falling back to
// received_qty when accepted is not yet populated), rate / discount /
// gst_rate copied from the GRN line snapshot, and measurements
// copied from the GRN row.
// ─────────────────────────────────────────────────────────────────

export function buildBillFromGrnLines(grnLines, defaults) {
  const list = Array.isArray(grnLines) ? grnLines : [];
  const opts = defaults && typeof defaults === 'object' ? defaults : {};
  const taxCtx = {
    supplier_state_code: pickStringUpper(opts, 'supplier_state_code', 'supplierStateCode'),
    company_state_code:  pickStringUpper(opts, 'company_state_code',  'companyStateCode')
  };

  return list.map((grn) => {
    const safe        = grn && typeof grn === 'object' ? grn : {};
    const grnLineId   = safe.id || pick(safe, 'grn_line_id', 'grnLineId',
                           pick(safe, 'goods_receipt_line_id', 'goodsReceiptLineId', null));
    const poLineId    = pick(safe, 'po_line_id', 'poLineId',
                           pick(safe, 'purchase_order_line_id', 'purchaseOrderLineId', null));
    const itemType    = pickStringUpper(safe, 'item_type', 'itemType');
    const uom         = pickStringUpper(safe, 'uom', 'uom');
    const acceptedQty = toNumber(pick(safe, 'accepted_qty', 'acceptedQty', undefined));
    const receivedQty = toNumber(pick(safe, 'received_qty', 'receivedQty', 0));
    const billQty     = acceptedQty > 0 ? acceptedQty : receivedQty;
    const widthMm     = toNumber(pick(safe, 'received_width_mm', 'receivedWidthMm',
                           pick(safe, 'width_mm', 'widthMm', 0)));
    const heightMm    = toNumber(pick(safe, 'received_height_mm', 'receivedHeightMm',
                           pick(safe, 'height_mm', 'heightMm', 0)));
    const lengthVal   = toNumber(pick(safe, 'received_length_value', 'receivedLengthValue',
                           pick(safe, 'length_value', 'lengthValue', 0)));
    const rate        = toNumber(pick(safe, 'rate',         'rate',        0));
    const discountPct = toNumber(pick(safe, 'discount_percent', 'discountPercent', 0));
    const gstRate     = toNumber(pick(safe, 'gst_rate',     'gstRate',     0));

    const draft = {
      goods_receipt_line_id:  grnLineId,
      goodsReceiptLineId:     grnLineId,
      purchase_order_line_id: poLineId,
      purchaseOrderLineId:    poLineId,

      item_id:    pick(safe, 'item_id',   'itemId',   null),
      itemId:     pick(safe, 'item_id',   'itemId',   null),
      item_code:  pick(safe, 'item_code', 'itemCode', null),
      itemCode:   pick(safe, 'item_code', 'itemCode', null),
      item_name:  pick(safe, 'item_name', 'itemName', null),
      itemName:   pick(safe, 'item_name', 'itemName', null),
      item_type:  itemType || null,
      itemType:   itemType || null,
      uom:        uom || null,

      bill_qty:          billQty,
      billQty,
      received_qty:      receivedQty,
      receivedQty,
      accepted_qty:      acceptedQty,
      acceptedQty,

      bill_rate:         rate,
      billRate:          rate,
      rate,
      discount_percent:  discountPct,
      discountPercent:   discountPct,
      gst_rate:          gstRate,
      gstRate,

      bill_width_mm:     widthMm,
      billWidthMm:       widthMm,
      bill_height_mm:    heightMm,
      billHeightMm:      heightMm,
      bill_length_value: lengthVal,
      billLengthValue:   lengthVal,

      hsn_code: pick(safe, 'hsn_code', 'hsnCode', null),
      sac_code: pick(safe, 'sac_code', 'sacCode', null),

      remarks: null
    };
    return calculatePurchaseBillLine(draft, taxCtx);
  });
}


// ─────────────────────────────────────────────────────────────────
// Last-mile validation — runs locally before opening the network
// call. Returns { ok, errors:[string] } so the page can surface a
// concise summary alert.
//
// Mirrors the Joi shape rules in purchaseBill.validation.js — the
// backend remains the authority and will reject anything we miss
// (including PO pending-balance overflow, duplicate invoice numbers,
// and over-billing, which we cannot check without server context).
// ─────────────────────────────────────────────────────────────────

export function validatePurchaseBillLine(line) {
  const errors = [];
  if (!line || typeof line !== 'object') {
    errors.push('Line is required.');
    return { ok: false, errors };
  }
  const itemType    = pickStringUpper(line, 'item_type', 'itemType');
  const uom         = pickStringUpper(line, 'uom', 'uom');
  const itemName    = pickStringTrim(line, 'item_name', 'itemName');
  const billQty     = toNumber(pick(line, 'bill_qty', 'billQty', 0));
  const rate        = toNumber(pick(line, 'bill_rate', 'billRate',
                       pick(line, 'rate', 'rate', 0)));
  const discountPct = toNumber(pick(line, 'discount_percent', 'discountPercent', 0));
  const gstRate     = toNumber(pick(line, 'gst_rate', 'gstRate', 0));

  if (!itemName) {
    errors.push('Stock item is required.');
  }
  if (!itemType) {
    errors.push('Item type is required.');
  } else if (!KNOWN_TYPES_SET.has(itemType)) {
    errors.push(`Item type must be one of: ${ITEM_TYPES.join(', ')}.`);
  }
  if (!uom) {
    errors.push('UOM is required.');
  }
  if (billQty <= 0) {
    errors.push('Billed quantity must be greater than 0.');
  }
  if (rate < 0) {
    errors.push('Rate cannot be negative.');
  }
  if (discountPct < 0 || discountPct > 100) {
    errors.push('Discount percent must be between 0 and 100.');
  }
  if (gstRate < GST_RATE_MIN || gstRate > GST_RATE_MAX) {
    errors.push(`GST rate must be between ${GST_RATE_MIN} and ${GST_RATE_MAX}.`);
  }

  if (itemType === ITEM_TYPE_GLASS) {
    const w = toNumber(pick(line, 'bill_width_mm',  'billWidthMm',
                       pick(line, 'width_mm',  'widthMm',  0)));
    const h = toNumber(pick(line, 'bill_height_mm', 'billHeightMm',
                       pick(line, 'height_mm', 'heightMm', 0)));
    if (w <= 0) {
      errors.push('Width is required for glass bill lines.');
    }
    if (h <= 0) {
      errors.push('Height is required for glass bill lines.');
    }
  } else if (PROFILE_TYPES_SET.has(itemType) && LENGTH_UOMS_SET.has(uom)) {
    const len = toNumber(pick(line, 'bill_length_value', 'billLengthValue',
                       pick(line, 'length_value', 'lengthValue', 0)));
    if (len <= 0) {
      errors.push('Length is required for profile bill lines.');
    }
  }

  return { ok: errors.length === 0, errors };
}

export function validatePurchaseBill(payload) {
  const errors = [];
  if (!payload || typeof payload !== 'object') {
    errors.push('Purchase Bill payload is required.');
    return { ok: false, errors };
  }

  const billDate    = pick(payload, 'bill_date',             'billDate',           null);
  const invoiceNo   = pickStringTrim(payload, 'supplier_invoice_no',   'supplierInvoiceNo');
  const invoiceDate = pick(payload, 'supplier_invoice_date', 'supplierInvoiceDate', null);
  const supplierName = pickStringTrim(payload, 'supplier_name', 'supplierName');
  const status      = pickStringUpper(payload, 'status', 'status');
  const matchStat   = pickStringUpper(payload, 'match_status', 'matchStatus');
  const matchType   = pickStringUpper(payload, 'matching_type', 'matchingType');
  const poId        = pick(payload, 'purchase_order_id', 'purchaseOrderId', null);
  const grnId       = pick(payload, 'goods_receipt_id',  'goodsReceiptId',  null);
  const lines       = Array.isArray(payload.lines) ? payload.lines : [];

  if (!billDate) {
    errors.push('Bill date is required.');
  }
  if (!supplierName) {
    errors.push('Supplier name is required.');
  }
  if (!invoiceNo) {
    errors.push('Supplier invoice number is required.');
  }
  if (!invoiceDate) {
    errors.push('Supplier invoice date is required.');
  }
  if (!poId && !grnId) {
    errors.push('Either a Purchase Order or a Goods Receipt must be linked to the bill.');
  }
  if (status && !PB_STATUS_SET.has(status)) {
    errors.push(`Status must be one of: ${PB_STATUSES.join(', ')}.`);
  }
  if (matchStat && !PB_MATCH_STATUS_SET.has(matchStat)) {
    errors.push(`Match status must be one of: ${PB_MATCH_STATUSES.join(', ')}.`);
  }
  if (matchType && !PB_MATCHING_TYPE_SET.has(matchType)) {
    errors.push(`Matching type must be one of: ${PB_MATCHING_TYPES.join(', ')}.`);
  }
  if (lines.length === 0) {
    errors.push('At least one bill line is required.');
  } else {
    lines.forEach((ln, idx) => {
      const r = validatePurchaseBillLine(ln);
      if (!r.ok) {
        for (const msg of r.errors) {
          errors.push(`Line ${idx + 1}: ${msg}`);
        }
      }
    });
  }

  return { ok: errors.length === 0, errors };
}


// ─────────────────────────────────────────────────────────────────
// Capability gates — drive toolbar enable/disable. The backend
// remains the authority; these guards only hide CTAs the operator
// can't possibly succeed with.
//
// Editable / cancellable: DRAFT or MISMATCH_HOLD.
// Re-match: DRAFT or MISMATCH_HOLD.
// Approve: only MATCHED.
// ─────────────────────────────────────────────────────────────────

function normStatus(status) {
  if (status === null || status === undefined) return '';
  if (typeof status === 'object') {
    return String(status.status || '').trim().toUpperCase();
  }
  return String(status).trim().toUpperCase();
}

export function canEditPurchaseBill(status) {
  const s = normStatus(status);
  return s === 'DRAFT' || s === 'MISMATCH_HOLD';
}

export function canMatchPurchaseBill(status) {
  const s = normStatus(status);
  return s === 'DRAFT' || s === 'MISMATCH_HOLD';
}

export function canApprovePurchaseBill(status) {
  return normStatus(status) === 'MATCHED';
}

export function canCancelPurchaseBill(status) {
  const s = normStatus(status);
  return s === 'DRAFT' || s === 'MISMATCH_HOLD';
}


// ─────────────────────────────────────────────────────────────────
// Status labels + tag colours. Colours align with the AntD Tag
// palette guidance in docs/23-antd-component-standards.md so the
// PB grids match the rest of the control-plane chrome.
// ─────────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  DRAFT:           'Draft',
  MATCHED:         'Matched',
  MISMATCH_HOLD:   'Mismatch (Hold)',
  APPROVED:        'Approved',
  POSTING_PENDING: 'Posting Pending',
  CANCELLED:       'Cancelled'
};

const STATUS_COLORS = {
  DRAFT:           'default',
  MATCHED:         'processing',
  MISMATCH_HOLD:   'warning',
  APPROVED:        'success',
  POSTING_PENDING: 'gold',
  CANCELLED:       'default'
};

const MATCH_STATUS_LABELS = {
  PENDING:           'Pending',
  MATCHED:           'Matched',
  MISMATCH:          'Mismatch',
  OVERRIDE_REQUIRED: 'Override Required'
};

const MATCH_STATUS_COLORS = {
  PENDING:           'default',
  MATCHED:           'success',
  MISMATCH:          'error',
  OVERRIDE_REQUIRED: 'warning'
};

export function statusLabel(status) {
  if (!status) return '';
  const key = String(status).trim().toUpperCase();
  return STATUS_LABELS[key] || key;
}

export function statusColor(status) {
  if (!status) return 'default';
  const key = String(status).trim().toUpperCase();
  return STATUS_COLORS[key] || 'default';
}

export function matchStatusLabel(status) {
  if (!status) return '';
  const key = String(status).trim().toUpperCase();
  return MATCH_STATUS_LABELS[key] || key;
}

export function matchStatusColor(status) {
  if (!status) return 'default';
  const key = String(status).trim().toUpperCase();
  return MATCH_STATUS_COLORS[key] || 'default';
}


// ─────────────────────────────────────────────────────────────────
// Match-result summary — backend purchaseBillMatching.service emits
// findings shaped like { type, severity, message, values } with
// severity ∈ { 'INFO', 'WARNING', 'BLOCKER' }. The bill detail page
// shows a count badge per severity bucket; this helper does the
// bucketing in one pass.
//
// Tolerant: ignores non-array input, ignores rows without a string
// severity, normalises severity to upper-case.
// ─────────────────────────────────────────────────────────────────

export function summarizeMatchResults(results) {
  const out = { blockers: 0, warnings: 0, info: 0 };
  const list = Array.isArray(results) ? results : [];
  for (const r of list) {
    if (!r || typeof r !== 'object') continue;
    const sev = String(r.severity || '').trim().toUpperCase();
    if (sev === 'BLOCKER')      out.blockers += 1;
    else if (sev === 'WARNING') out.warnings += 1;
    else if (sev === 'INFO')    out.info     += 1;
  }
  return out;
}
